import { onBeforeUnmount, unref, watchEffect } from 'vue';

const ensureMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.append(element);
  }
  Object.entries(attributes).forEach(([name, value]) => {
    if (value) element.setAttribute(name, value);
  });
};

const ensureCanonical = () => {
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.append(canonical);
  }
  return canonical;
};

export function usePageSeo(metaSource) {
  const previousTitle = document.title;
  // A static JSON-LD block can ship in index.html for pre-hydration crawlers;
  // once this composable takes over as the source of truth, drop it so the
  // page doesn't end up with two competing structured-data blocks.
  document.head.querySelectorAll('script[type="application/ld+json"]:not([data-page-schema])').forEach(el => el.remove());
  const schema = document.createElement('script');
  schema.type = 'application/ld+json';
  schema.dataset.pageSchema = 'true';
  document.head.append(schema);

  const stop = watchEffect(() => {
    const meta = unref(metaSource);
    if (!meta) return;
    document.title = meta.title;
    ensureMeta('meta[name="description"]', { name:'description', content:meta.description });
    ensureMeta('meta[name="robots"]', { name:'robots', content:meta.robots || 'index,follow' });
    ensureMeta('meta[property="og:type"]', { property:'og:type', content:meta.ogType || 'website' });
    ensureMeta('meta[property="og:title"]', { property:'og:title', content:meta.ogTitle || meta.title });
    ensureMeta('meta[property="og:description"]', { property:'og:description', content:meta.ogDescription || meta.description });
    ensureMeta('meta[property="og:url"]', { property:'og:url', content:meta.canonical });
    ensureMeta('meta[property="og:image"]', { property:'og:image', content:meta.ogImage || meta.image });
    ensureMeta('meta[name="twitter:card"]', { name:'twitter:card', content:'summary_large_image' });
    ensureMeta('meta[name="twitter:title"]', { name:'twitter:title', content:meta.ogTitle || meta.title });
    ensureMeta('meta[name="twitter:description"]', { name:'twitter:description', content:meta.ogDescription || meta.description });
    ensureMeta('meta[name="twitter:image"]', { name:'twitter:image', content:meta.ogImage || meta.image });
    ensureCanonical().href = meta.canonical;
    schema.dataset.pageSchema = meta.path || 'page';
    schema.textContent = JSON.stringify(meta.schema || {});
  });

  onBeforeUnmount(() => {
    stop();
    document.title = previousTitle;
    schema.remove();
  });
}
